/**
 * Global Excel Configurator - Universal Configuration Hook
 *
 * Generic hook for managing excel configurations with any data source.
 */

import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import type {
  ExcelConfiguration,
  SavedConfiguration,
  DataSourceAdapter,
  RowData,
} from "../types";
import { getAdapter } from "../adapters";
import { api } from "@/lib/api-typed";
import { apiErrorMessage } from "@/lib/error-handling/api-error-message";

import { downloadGeneratedExcel } from "./excel-download";
import type {
  UseExcelConfigurationOptions,
  UseExcelConfigurationReturn,
} from "./use-excel-configuration.types";

export type { UseExcelConfigurationOptions, UseExcelConfigurationReturn };

// ============================================================================
// Hook
// ============================================================================

export function useExcelConfiguration<TSource = any, TRow extends RowData = RowData>({
  dataSourceType,
  clientId,
  entityId,
  autoLoad = true,
  adapter: customAdapter,
}: UseExcelConfigurationOptions<TSource>): UseExcelConfigurationReturn<TSource> {
  // Get adapter from registry or use custom
  const adapter = customAdapter ?? getAdapter<DataSourceAdapter<TSource, TRow>>(dataSourceType);

  // State
  const [configuration, setConfiguration] = useState<ExcelConfiguration<TSource>>(
    adapter.getDefaultConfiguration()
  );
  const [configurations, setConfigurations] = useState<SavedConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ============================================================================
  // Configuration Management
  // ============================================================================

  const updateConfiguration = useCallback(
    (updates: Partial<ExcelConfiguration<TSource>>) => {
      setConfiguration((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const resetConfiguration = useCallback(() => {
    setConfiguration(adapter.getDefaultConfiguration());
  }, [adapter]);

  const createNewConfiguration = useCallback(
    (name: string, description = "") => {
      const defaultConfig = adapter.getDefaultConfiguration();
      setConfiguration({
        ...defaultConfig,
        name,
        description,
      });
    },
    [adapter]
  );

  const resetToDefault = useCallback(() => {
    const defaultConfig = adapter.getDefaultConfiguration();
    setConfiguration(defaultConfig);
    toast.success("Configuration reset to default");
  }, [adapter]);

  // ============================================================================
  // API Operations
  // ============================================================================

  const loadConfigurations = useCallback(async () => {
    if (!clientId) return;

    setIsLoading(true);
    try {
      const data: SavedConfiguration[] = await api.get("/clients/{client_id}/excel-configurations", {
        path: { client_id: clientId },
        query: { data_source_type: dataSourceType },
      });
      setConfigurations(data);

      // Auto-load default or first configuration
      if (autoLoad && data.length > 0) {
        const defaultConfig = data.find((c) => c.is_default) || data[0];
        setConfiguration({
          id: defaultConfig.id,
          name: defaultConfig.name,
          description: defaultConfig.description ?? "",
          columns: defaultConfig.columns,
          options: defaultConfig.options ?? undefined,
          isDefault: defaultConfig.is_default,
          dataSourceType: defaultConfig.data_source_type,
        });
      }
    } catch (error: unknown) {
      console.error("Error loading configurations:", error);
      toast.error(apiErrorMessage(error, "Failed to load configurations"));
    } finally {
      setIsLoading(false);
    }
  }, [clientId, dataSourceType, autoLoad]);

  const loadConfiguration = useCallback(async (configId: string) => {
    const config = configurations.find((c) => c.id === configId);
    if (!config) {
      toast.error("Configuration not found");
      return;
    }

    setConfiguration({
      id: config.id,
      name: config.name,
      description: config.description ?? "",
      columns: config.columns,
      options: config.options ?? undefined,
      isDefault: config.is_default,
      dataSourceType: config.data_source_type,
    });
    toast.success(`Loaded: ${config.name}`);
  }, [configurations]);

  const saveConfiguration = useCallback(
    async (config?: ExcelConfiguration<TSource>) => {
      const configToSave = config || configuration;

      if (!clientId) {
        toast.error("Client ID is required to save configuration");
        return;
      }

      if (!configToSave.name.trim()) {
        toast.error("Configuration name is required");
        return;
      }

      if (configToSave.columns.length === 0) {
        toast.error("Add at least one column");
        return;
      }

      // Validate configuration
      const validation = adapter.validateConfiguration(configToSave);
      if (!validation.valid) {
        toast.error(`Validation failed: ${validation.errors[0]?.message}`);
        return;
      }

      setIsSaving(true);
      try {
        const payload = {
          name: configToSave.name,
          description: configToSave.description,
          columns: configToSave.columns,
          options: configToSave.options,
          is_default: configToSave.isDefault || false,
          data_source_type: dataSourceType,
        };

        const savedConfig: SavedConfiguration = configToSave.id
          ? await api.put("/clients/{client_id}/excel-configurations/{configuration_id}", {
              path: { client_id: clientId, configuration_id: configToSave.id },
              body: payload,
            })
          : await api.post("/clients/{client_id}/excel-configurations", { path: { client_id: clientId }, body: payload });

        // Update local state
        if (configToSave.id) {
          setConfigurations((prev) =>
            prev.map((c) => (c.id === savedConfig.id ? savedConfig : c))
          );
        } else {
          setConfigurations((prev) => [...prev, savedConfig]);
        }

        setConfiguration({
          id: savedConfig.id,
          name: savedConfig.name,
          description: savedConfig.description ?? "",
          columns: savedConfig.columns,
          options: savedConfig.options ?? undefined,
          isDefault: savedConfig.is_default,
          dataSourceType: savedConfig.data_source_type,
        });

        toast.success(configToSave.id ? "Configuration updated!" : "Configuration saved!");
        return savedConfig;
      } catch (error: unknown) {
        console.error("Error saving configuration:", error);
        toast.error(apiErrorMessage(error, "Failed to save configuration"));
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [clientId, configuration, dataSourceType, adapter]
  );

  const deleteConfiguration = useCallback(
    async (configId: string) => {
      if (!clientId) return;

      try {
        await api.delete("/clients/{client_id}/excel-configurations/{configuration_id}", {
          path: { client_id: clientId, configuration_id: configId },
        });

        setConfigurations((prev) => prev.filter((c) => c.id !== configId));

        if (configuration.id === configId) {
          resetConfiguration();
        }

        toast.success("Configuração eliminada.");
      } catch (error: unknown) {
        console.error("Error deleting configuration:", error);
        toast.error(apiErrorMessage(error, "Não foi possível eliminar a configuração. Tenta outra vez."));
        throw error;
      }
    },
    [clientId, configuration.id, resetConfiguration]
  );

  const duplicateConfiguration = useCallback(
    async (configId: string, newName: string) => {
      const config = configurations.find((c) => c.id === configId);
      if (!config) {
        toast.error("Configuration not found");
        return;
      }

      const duplicated: ExcelConfiguration<TSource> = {
        name: newName,
        description: config.description ?? "",
        columns: JSON.parse(JSON.stringify(config.columns)), // Deep clone
        options: config.options ?? undefined,
        dataSourceType: config.data_source_type,
      };

      await saveConfiguration(duplicated);
    },
    [configurations, saveConfiguration]
  );

  // ============================================================================
  // Import/Export
  // ============================================================================

  const exportConfiguration = useCallback(() => {
    const exportData = {
      name: configuration.name,
      description: configuration.description,
      columns: configuration.columns,
      options: configuration.options,
      dataSourceType,
      exportedAt: new Date().toISOString(),
    };

    return JSON.stringify(exportData, null, 2);
  }, [configuration, dataSourceType]);

  const importConfiguration = useCallback(
    (json: string) => {
      try {
        const imported = JSON.parse(json);

        if (!imported.name || !imported.columns) {
          throw new Error("Invalid configuration format");
        }

        setConfiguration({
          name: imported.name,
          description: imported.description || "",
          columns: imported.columns,
          options: imported.options,
          dataSourceType,
        });

        toast.success("Configuration imported successfully");
      } catch (error: unknown) {
        console.error("Error importing configuration:", error);
        toast.error(apiErrorMessage(error, "Failed to import configuration"));
        throw error;
      }
    },
    [dataSourceType]
  );

  // ============================================================================
  // Excel Generation
  // ============================================================================

  const generateExcel = useCallback(
    async (source: TSource) => {
      if (!entityId) {
        toast.error("Entity ID is required for Excel generation");
        return;
      }

      if (configuration.columns.length === 0) {
        toast.error("Configura pelo menos uma coluna");
        return;
      }

      // Validate configuration
      const validation = adapter.validateConfiguration(configuration);
      if (!validation.valid) {
        toast.error(`Validation failed: ${validation.errors[0]?.message}`);
        return;
      }

      try {
        await downloadGeneratedExcel({ source, adapter, configuration, dataSourceType, entityId });

        toast.success("Excel generated successfully!");
      } catch (error: unknown) {
        console.error("Error generating Excel:", error);
        toast.error(apiErrorMessage(error, "Failed to generate Excel"));
        throw error;
      }
    },
    [entityId, configuration, dataSourceType, adapter]
  );

  // ============================================================================
  // Validation
  // ============================================================================

  const validateConfiguration = useCallback(() => {
    const result = adapter.validateConfiguration(configuration);
    return {
      valid: result.valid,
      errors: result.errors.map((e) => e.message),
      warnings: result.warnings.map((w) => w.message),
    };
  }, [adapter, configuration]);

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    if (clientId && autoLoad) {
      loadConfigurations();
    }
  }, [clientId, autoLoad, loadConfigurations]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    configuration,
    configurations,
    isLoading,
    isSaving,

    // Configuration Management
    setConfiguration,
    updateConfiguration,
    resetConfiguration,

    // CRUD Operations
    loadConfigurations,
    loadConfiguration,
    saveConfiguration,
    deleteConfiguration,
    duplicateConfiguration,

    // Helpers
    createNewConfiguration,
    exportConfiguration,
    importConfiguration,
    resetToDefault,

    // Excel Generation
    generateExcel,

    // Validation
    validateConfiguration,

    // Adapter
    adapter,
  };
}
